<template>
  <div class="og-image-preview">
    <button
      type="button"
      :class="{ active: showPreview }"
      @click="togglePreview()"
    >
      <Icon
        :size="16"
        :name="showPreview ? 'fa6-regular:eye' : 'fa6-regular:eye-slash'"
      />
      <span>Preview <strong>og:image</strong></span>
    </button>
    <div v-if="showPreview && imageUrl" class="og-image-preview__inner">
      <img
        :src="imageUrl"
        alt="og:image preview"
        :width="600"
        :height="315"
        :class="{ loaded: imageLoaded }"
        @load="imageLoaded = true"
        @error="imageLoaded = true"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  slug: string
}>()

const { url } = useSiteConfig()
const showPreview = ref(false)
const isMounted = ref(false)
const imageLoaded = ref(false)

const imageUrl = computed(() => {
  if (!isMounted.value) return ''
  return `${url}/__og-image__/image/${props.slug}/og.png?purge`
})

function togglePreview() {
  showPreview.value = !showPreview.value
  // Reset image loaded state when toggling on
  if (showPreview.value) {
    imageLoaded.value = false
  }
}

onMounted(() => {
  isMounted.value = true
})
</script>

<style lang="less" scoped>
.og-image-preview {
  position: fixed;
  bottom: 0;
  right: 0;
  margin: @18px;
  z-index: 999999;

  button {
    position: absolute;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    gap: @3px;
    background: white;
    font-size: 12px;
    border: 1px solid @color-border;
    color: @color-text-body;
    padding: 0 @6px 0 @3px;
    transition: border 0.3s ease-out;
    white-space: nowrap;
    z-index: 9999999;
    border-radius: 6px;
    cursor: pointer;

    &:hover,
    &:active {
      border-color: @color-form-border;
    }

    > span {
      border-left: 1px solid @color-border;
      padding: @6px 0 @6px @6px;

      &.iconify {
        border: 0;
        display: block;
        padding: 0;
        min-width: @24px;
      }
    }

    &.active {
      border-color: @color-primary;
      > span.iconify {
        color: @color-primary;
      }
    }
  }

  .og-image-preview__inner {
    position: relative;
    padding: @12px;
    box-sizing: content-box;
    background: white;
    box-shadow: 0 0 24px 2px rgba(0, 0, 0, 0.075);
    width: 600px;
    height: 315px;
    transform: translateY(-45px);

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 40px;
      height: 40px;
      margin: -20px 0 0 -20px;
      border: 4px solid #f0f0f0;
      border-top-color: #333;
      border-radius: 50%;
      animation: spinner 0.8s linear infinite;
    }

    img {
      position: relative;
      z-index: 1;
      background: white;
      opacity: 0;
      transition: opacity 0.2s ease-in;

      &.loaded {
        opacity: 1;
      }
    }
  }

  @keyframes spinner {
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
